import React from 'react';
import styled, { css } from 'styled-components';
import { TABLET_TEXT_WIDTH, LAPTOP_TEXT_WIDTH, FULLHD_TEXT_WIDTH } from '@constants/params';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { textSize, Points, marginLevel, paddingLevel } from '@styles';

export const CTABox = ({ html = '', children }) => (
  <>{html ? <View dangerouslySetInnerHTML={{ __html: html }} /> : <View>{children}</View>}</>
);

export const styles = {
  container: css`
    ${paddingLevel('both', 'accent_block')};
    ${marginLevel('both', 'accent_outer')};
    padding-left: 16px;
    padding-right: 16px;
    background-color: ${Colors.main_beige};

    &:has(+ h2) {
      ${marginLevel('top', 'blog_header')};
    }

    .visual-heading,
    strong {
      ${textSize('card_header')};
      margin-bottom: 10px;
      color: ${Colors.main_dark};
      display: block;
      font-weight: 300;
    }

    b {
      font-weight: 400;
    }

    p {
      width: 100%;
      ${textSize('body')};
      font-weight: 400;
      color: ${Colors.main_gray};
      padding: 0;
      margin-left: 0;
      margin-right: 0;

      &:last-child {
        margin-bottom: 0;
      }
    }

    p > strong {
      display: inline;
      font-weight: 500;
      ${textSize('body')};
    }

    .cta-button {
      width: 100%;
      height: 50px;
      margin: 30px 0 15px 0;
      padding: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      outline: none;
      text-align: center;
      font-size: 16px;
      font-weight: 400;
      border-radius: 9px;
      box-sizing: border-box;
      text-decoration: none;
      transition-property: background-color, border-color, box-shadow, background-position;
      transition-duration: 0.3s;
      transition-timing-function: ease;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -webkit-tap-highlight-color: transparent;
      touch-action: none;

      &:focus,
      &:active {
        outline: none;
        box-shadow: none;
      }

      &:first-child {
        margin-top: 30px;
      }

      &.primary {
        color: ${Colors.white};
        background-color: ${Colors.main_dark};
      }

      &.primary:hover {
        background-color: ${Colors.main_gray};
      }

      &.primary:active {
        background-color: ${Colors.main_black};
      }

      &.secondary {
        border: 1px solid ${Colors.main_dark};
        color: ${Colors.main_dark};
        background-color: ${Colors.white};
      }

      &.secondary:hover {
        background-color: ${Colors.dark_50};
      }

      &.secondary:active {
        background-color: ${Colors.dark_200};
        border: 1px solid ${Colors.main_black};
        color: ${Colors.main_black};
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    .cta-button-wrapper {
      margin-top: 40px;
    }

    .cta-button-wrapper .cta-button {
      margin-top: 0;
      margin-bottom: 0;
    }

    .cta-button-wrapper .cta-button:not(:first-child) {
      margin-top: 10px;
    }

    .double-button-wrapper {
      margin-top: 16px;
      display: flex;
      flex-direction: column;
    }

    .cta-button-text {
      width: 100%;
      padding-top: 8px;
      padding-bottom: 10px;
      text-align: center;
      font-size: 14px;
      line-height: 1.115;
      color: ${Colors.dark_500};
      outline: none;
      text-decoration: none;
      border: none;
      background: transparent;
    }

    .cta-button-text:hover,
    .cta-button-text:focus {
      color: ${Colors.main_gray};
    }

    @media ${Points.tablet_portrait} {
      width: ${TABLET_TEXT_WIDTH};
      padding-left: 25px;
      padding-right: 25px;
      margin-left: auto;
      margin-right: auto;

      .double-button-wrapper {
        margin-top: 0;
      }

      .cta-button {
        width: fit-content;
        margin-top: 30px;
        font-size: 16px;
        line-height: 18px;
      }

      .cta-button-text {
        width: fit-content;
        padding-bottom: 5px;
      }

      .cta-button-wrapper {
        display: flex;
        margin-top: 30px;
        gap: 15px;
      }

      .cta-button-wrapper .cta-button,
      .cta-button-wrapper .cta-button:not(:first-child) {
        margin-top: 0;
      }
    }

    @media ${Points.laptop} {
      width: ${LAPTOP_TEXT_WIDTH};
      padding-left: 30px;
      padding-right: 30px;
      background-color: ${Colors.white};
      border: 1px solid ${Colors.dark_500};

      .cta-button {
        width: fit-content;
      }

      .cta-button-text {
        padding-top: 5px;
        padding-bottom: 0;
        width: auto;
      }
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_TEXT_WIDTH};
      padding-left: 35px;
      padding-right: 35px;

      .cta-button {
        margin: 40px 15px 0 0;
      }

      .cta-button-wrapper {
        margin-top: 40px;
      }

      .cta-button-wrapper .cta-button {
        margin-top: 0;
      }
    }
  `,
};

export const View = styled.div`
  ${styles.container};
`;
